import { useEffect, useState } from "react";

/**
 * Identidade do app no frontend (nome/empresa) - singleton simples atualizado
 * assim que `/api/health` responde. Existe porque nem todo lugar que precisa
 * do nome do app tem o `HealthInfo` completo à mão (telas antes do login,
 * utilitários de exportação fora de componente).
 */
export const COLOR_THEMES = ["laranja", "vermelho", "azul", "verde"] as const;
export type ColorTheme = (typeof COLOR_THEMES)[number];

export interface AppBranding {
  appName: string;
  companyName: string;
  exampleQuestionsBusiness: string[];
  exampleQuestionsTechnical: string[];
  supportExampleQuestions: string[];
  aboutExampleQuestions: string[];
  colorTheme: ColorTheme;
  logoDataUrl: string;
}

/**
 * Placeholder neutro mostrado só no instante antes do /api/health responder -
 * nunca deve favorecer nenhum tenant específico (já foi "Educon Assistant"
 * fixo, e aparecia pra quem acessava QUALQUER domínio, não só o da Educon).
 */
export const DEFAULT_BRANDING: AppBranding = {
  appName: "Assistant",
  companyName: "",
  exampleQuestionsBusiness: [],
  exampleQuestionsTechnical: [],
  supportExampleQuestions: [],
  aboutExampleQuestions: [],
  colorTheme: "laranja",
  logoDataUrl: "",
};

let current: AppBranding = { ...DEFAULT_BRANDING };
const listeners = new Set<(b: AppBranding) => void>();

function isColorTheme(value: unknown): value is ColorTheme {
  return typeof value === "string" && (COLOR_THEMES as readonly string[]).includes(value);
}

export function getBranding(): AppBranding {
  return current;
}

export function setBranding(patch: Partial<AppBranding>): void {
  current = {
    appName: patch.appName || current.appName,
    companyName: patch.companyName || current.companyName,
    exampleQuestionsBusiness: patch.exampleQuestionsBusiness?.length
      ? patch.exampleQuestionsBusiness
      : current.exampleQuestionsBusiness,
    exampleQuestionsTechnical: patch.exampleQuestionsTechnical?.length
      ? patch.exampleQuestionsTechnical
      : current.exampleQuestionsTechnical,
    supportExampleQuestions: patch.supportExampleQuestions?.length
      ? patch.supportExampleQuestions
      : current.supportExampleQuestions,
    aboutExampleQuestions: patch.aboutExampleQuestions?.length
      ? patch.aboutExampleQuestions
      : current.aboutExampleQuestions,
    colorTheme: isColorTheme(patch.colorTheme) ? patch.colorTheme : current.colorTheme,
    logoDataUrl: typeof patch.logoDataUrl === "string" ? patch.logoDataUrl : current.logoDataUrl,
  };
  document.documentElement.setAttribute("data-color-theme", current.colorTheme);
  listeners.forEach((l) => l(current));
}

/** Nome de arquivo seguro derivado do nome do app (ex.: "Educon Assistant" -> "educon-assistant"). */
export function brandingSlug(): string {
  return current.appName
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** Busca a identidade uma vez (telas antes do login não passam por useChat). */
export function useBranding(): AppBranding {
  const [branding, setLocal] = useState(current);
  useEffect(() => {
    listeners.add(setLocal);
    fetch("/api/health")
      .then((r) => r.json())
      .then((j: Partial<AppBranding>) => setBranding(j))
      .catch(() => {});
    return () => {
      listeners.delete(setLocal);
    };
  }, []);
  return branding;
}
